import { useCallback, useEffect, useMemo, useState } from "react";
import { apiFetch } from "../../../config/api";
import { formatDate } from "../../../config/utils";
import EmptyState from "../../../components/EmptyState";
import Loader from "../../../components/Loader";
import PageHeader from "../../../components/PageHeader";
import Pagination from "../../../components/Pagination";
import { ClientPortalBadge, todayInput } from "../clientPortalUtils";

const PAGE_SIZE = 10;
const STATUSES = ["All", "Pending", "Approved", "Rejected", "Changes Requested"];
const EMPTY_FORM = {
  clientAccountId: "",
  projectId: "",
  title: "",
  approvalType: "Deliverable",
  dueDate: todayInput(7),
  notes: "",
};

export default function ClientApprovalsPage() {
  const [approvals, setApprovals] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("All");
  const [accountId, setAccountId] = useState("");
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const loadLookups = useCallback(async () => {
    try {
      const [accountsRes, projectsRes] = await Promise.all([
        apiFetch("/api/client-portal/accounts"),
        apiFetch("/api/projects"),
      ]);
      if (accountsRes.ok) setAccounts(await accountsRes.json());
      if (projectsRes.ok) setProjects(await projectsRes.json());
    } catch {
      setAccounts([]);
      setProjects([]);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      if (status !== "All") params.set("status", status);
      if (accountId) params.set("accountId", accountId);
      const res = await apiFetch(`/api/client-portal/approvals?${params.toString()}`);
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setApprovals(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.message || "Could not load approvals.");
    } finally {
      setLoading(false);
    }
  }, [accountId, status]);

  useEffect(() => { loadLookups(); }, [loadLookups]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [accountId, status]);

  const totalPages = Math.max(1, Math.ceil(approvals.length / PAGE_SIZE));
  const pagedApprovals = useMemo(() => {
    const safePage = Math.min(page, totalPages);
    return approvals.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  }, [approvals, page, totalPages]);

  function updateField(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  function selectAccount(nextAccountId) {
    const account = accounts.find((item) => item.id === nextAccountId);
    setForm((current) => ({
      ...current,
      clientAccountId: nextAccountId,
      projectId: account?.linkedProjectId || current.projectId,
    }));
  }

  function openCreate() {
    setForm({
      ...EMPTY_FORM,
      clientAccountId: accountId || "",
      dueDate: todayInput(7),
    });
    setModalOpen(true);
  }

  async function createApproval(event) {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = {
        ...form,
        projectId: form.projectId || null,
        dueDate: form.dueDate ? `${form.dueDate}T00:00:00` : null,
      };
      const res = await apiFetch("/api/client-portal/approvals", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(await res.text());
      setModalOpen(false);
      await load();
    } catch (err) {
      setError(err?.message || "Could not create approval.");
    } finally {
      setSaving(false);
    }
  }

  async function updateStatus(approval, nextStatus) {
    setSaving(true);
    setError("");
    try {
      const res = await apiFetch(`/api/client-portal/approvals/${approval.id}/status`, {
        method: "PATCH",
        body: JSON.stringify({
          status: nextStatus,
          respondedByName: approval.respondedByName || approval.clientName,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      await load();
    } catch (err) {
      setError(err?.message || "Could not update approval.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="client-portal-page">
      <PageHeader
        title="Approvals"
        subtitle={`${approvals.length} approval${approvals.length === 1 ? "" : "s"} in the current view.`}
        actions={<button className="primary" onClick={openCreate}>Request Approval</button>}
      />

      <div className="crm-toolbar glass-card" style={{ marginBottom: 16 }}>
        <div className="crm-search">
          <select value={accountId} onChange={(event) => setAccountId(event.target.value)} aria-label="Filter by client">
            <option value="">All clients</option>
            {accounts.map((account) => <option key={account.id} value={account.id}>{account.name}</option>)}
          </select>
        </div>
        <div className="crm-stage-filter" aria-label="Approval status">
          {STATUSES.map((item) => (
            <button key={item} type="button" className={status === item ? "active" : ""} onClick={() => setStatus(item)}>
              {item}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="error-banner" style={{ marginBottom: 16 }}>{error}</div>}

      <div className="glass-card client-portal-table-card">
        {loading ? (
          <div style={{ padding: 20 }}>
            {[1, 2, 3, 4, 5].map((item) => <Loader key={item} type="skeleton" height={48} style={{ marginBottom: 10 }} />)}
          </div>
        ) : approvals.length === 0 ? (
          <EmptyState
            title="No approvals"
            subtitle="Request client approvals for milestones, documents, change requests, or deliverables."
            action={<button className="primary" onClick={openCreate}>Request Approval</button>}
          />
        ) : (
          <>
            <div className="client-portal-table-wrap">
              <table className="client-portal-table">
                <thead>
                  <tr>
                    <th>Approval</th>
                    <th>Client</th>
                    <th>Due</th>
                    <th>Status</th>
                    <th>Response</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {pagedApprovals.map((approval) => (
                    <tr key={approval.id}>
                      <td>
                        <div className="client-portal-row-title">{approval.title}</div>
                        <div className="client-portal-row-subtitle">{approval.approvalType} - {approval.projectName || "No project"}</div>
                      </td>
                      <td>{approval.clientName}</td>
                      <td>{formatDate(approval.dueDate)}</td>
                      <td><ClientPortalBadge status={approval.status} /></td>
                      <td>
                        <div>{approval.respondedByName || "-"}</div>
                        <div className="client-portal-row-subtitle">{approval.respondedAt ? formatDate(approval.respondedAt) : ""}</div>
                      </td>
                      <td>
                        {approval.status === "Pending" ? (
                          <div className="client-portal-action-row">
                            <button className="btn-success" onClick={() => updateStatus(approval, "Approved")} disabled={saving}>Approve</button>
                            <button className="btn-ghost" onClick={() => updateStatus(approval, "Changes Requested")} disabled={saving}>Changes</button>
                            <button className="btn-danger" onClick={() => updateStatus(approval, "Rejected")} disabled={saving}>Reject</button>
                          </div>
                        ) : (
                          <button className="btn-ghost" onClick={() => updateStatus(approval, "Pending")} disabled={saving}>Reopen</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={Math.min(page, totalPages)} totalPages={totalPages} onPageChange={setPage} total={approvals.length} pageSize={PAGE_SIZE} />
          </>
        )}
      </div>

      {modalOpen && (
        <div className="modal-overlay" onClick={() => setModalOpen(false)}>
          <form className="glass-card modal crm-modal" onSubmit={createApproval} onClick={(event) => event.stopPropagation()}>
            <div className="modal-header">
              <h3>Request Approval</h3>
              <button type="button" className="modal-close" onClick={() => setModalOpen(false)} aria-label="Close">x</button>
            </div>
            <div className="crm-form-grid">
              <label>
                Client
                <select value={form.clientAccountId} onChange={(event) => selectAccount(event.target.value)} required>
                  <option value="">Select client</option>
                  {accounts.map((account) => <option key={account.id} value={account.id}>{account.name}</option>)}
                </select>
              </label>
              <label>
                Project
                <select value={form.projectId} onChange={(event) => updateField("projectId", event.target.value)}>
                  <option value="">Use client default</option>
                  {projects.map((project) => <option key={project.id} value={project.id}>{project.name}</option>)}
                </select>
              </label>
              <label>
                Title
                <input value={form.title} onChange={(event) => updateField("title", event.target.value)} required />
              </label>
              <label>
                Approval Type
                <input value={form.approvalType} onChange={(event) => updateField("approvalType", event.target.value)} />
              </label>
              <label>
                Due Date
                <input type="date" value={form.dueDate} onChange={(event) => updateField("dueDate", event.target.value)} />
              </label>
              <label className="crm-form-wide">
                Notes
                <textarea value={form.notes} onChange={(event) => updateField("notes", event.target.value)} rows={3} />
              </label>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn-ghost" onClick={() => setModalOpen(false)}>Cancel</button>
              <button type="submit" className="primary" disabled={saving}>{saving ? "Saving..." : "Create Approval"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
